import { DataTable } from '@p1plug/ui';
import { C, Code, DocPage, H2, P, Snippet } from '../doc';
import { readEvents, registerWebhook, verifyWebhook, webhookRequest } from '../samples';

const EVENTS = [
  {
    type: 'order.status_changed',
    when: 'An order moves to another status',
    carries: 'order_id, external_order_id, previous_status, status, device_serial, tracking_code',
  },
  {
    type: 'installation.activated',
    when: 'A device connects for the first time after delivery',
    carries: 'installation_id, meter_point_id, external_order_id, device_serial, meter_id, activated_at',
  },
  {
    type: 'installation.replaced',
    when: 'A replacement device takes over a meter point',
    carries: 'meter_point_id, previous and new installation_id and device_serial',
  },
  {
    type: 'telemetry.electricity',
    when: 'A smart-meter reading arrives',
    carries: 'meter_point_id, seq, event_time, power, cumulative energy per tariff, phases, backfill',
  },
];

const GUARANTEES = [
  { what: 'Order', how: 'Events of your organisation are delivered in the order they were recorded.' },
  { what: 'At least once', how: 'A batch is repeated until you answer 2xx. Deduplicate on event_id.' },
  { what: 'Batches', how: 'Up to 50 events per request, as one JSON array.' },
  { what: 'Timeout', how: 'Your endpoint has 5 seconds to answer. Acknowledge first, do slow work afterwards.' },
  { what: 'Retries', how: 'Exponential backoff with jitter, from 1 second up to 5 minutes between attempts.' },
  {
    what: 'Giving up',
    how: 'After 100 failed attempts delivery pauses. Nothing is lost: resume it, or read the log with the cursor API.',
  },
];

export function Webhooks() {
  return (
    <DocPage
      title="Webhooks and events"
      lead="P1Plug records everything that happens to your orders and devices in one event log. You can have it pushed to you, pull it yourself, or both."
    >
      <H2>Events</H2>
      <DataTable
        rowKey={(e) => e.type}
        columns={[
          { key: 'type', label: 'Type', mono: true },
          { key: 'when', label: 'Sent when' },
          { key: 'carries', label: 'data contains' },
        ]}
        rows={EVENTS}
      />
      <P>
        Every event has the same envelope: <C>event_id</C> (stable across redeliveries), <C>schema_version</C>,{' '}
        <C>type</C>, <C>tenant_id</C>, <C>occurred_at</C>, <C>cursor</C> and <C>data</C>.
      </P>

      <H2>Register an endpoint</H2>
      <P>
        One endpoint per organisation. Choose a secret of at least 16 characters; it signs every delivery. Use{' '}
        <C>event_types</C> to receive only what you handle: telemetry is by far the largest stream.
      </P>
      <Code samples={registerWebhook} />

      <H2>What a delivery looks like</H2>
      <Snippet title="HTTP request to your endpoint" code={webhookRequest} />

      <H2>Verify and handle</H2>
      <P>
        <C>X-P1Plug-Signature</C> is <C>sha256=</C> followed by the hex HMAC-SHA256 of the raw request body, keyed with
        your secret. Compute it over the bytes you received, before parsing, and compare in constant time.
      </P>
      <Code samples={verifyWebhook} />

      <H2>Delivery guarantees</H2>
      <DataTable
        rowKey={(g) => g.what}
        columns={[
          { key: 'what', label: 'Property' },
          { key: 'how', label: 'What P1Plug does' },
        ]}
        rows={GUARANTEES}
      />

      <H2 id="cursor">Pull with the cursor API</H2>
      <P>
        The cursor API reads the same log. Each page returns a <C>next_cursor</C>; store it and pass it as <C>after</C>{' '}
        next time. Use it instead of webhooks if you prefer polling, or next to them to catch up after an outage: every
        webhook event carries the <C>cursor</C> of its position, so you can switch from push to pull at exactly that
        point.
      </P>
      <Code samples={readEvents} />
      <P>
        Cursors are opaque and bound to your organisation. A cursor that was altered or belongs to someone else answers{' '}
        <C>400 invalid_cursor</C>.
      </P>

      <H2>When your endpoint is down</H2>
      <ol className="p1x-prose">
        <li>Deliveries fail and are retried with growing pauses. Ingestion of meter data is not affected.</li>
        <li>
          You see the state with <C>GET /api/v1/webhooks/endpoint</C> (<C>healthy</C>, <C>pending</C>, <C>last_error</C>
          ) and each attempt with <C>GET /api/v1/webhooks/deliveries</C>.
        </li>
        <li>When your endpoint answers again, the backlog is delivered in order.</li>
        <li>
          If delivery paused after 100 attempts (<C>stalled: true</C>), resume it with{' '}
          <C>POST /api/v1/webhooks/deliveries/retry</C>.
        </li>
      </ol>
    </DocPage>
  );
}
